export type Point = {
  x: number
  y: number
}
export type Edge = {
  start: Point
  end: Point
}
export type Polygon = Point[]

export function polygonToAABB(polygon: Polygon): Polygon {
  const xs = polygon.map((p) => p.x)
  const ys = polygon.map((p) => p.y)
  const minX = Math.min(...xs), maxX = Math.max(...xs)
  const minY = Math.min(...ys), maxY = Math.max(...ys)
  return [
    { x: minX, y: minY },
    { x: maxX, y: minY },
    { x: maxX, y: maxY },
    { x: minX, y: maxY },
  ]
}

export function interpolatePolygon(polygonA: Polygon, polygonB: Polygon, t: number): Polygon {
  if (polygonA.length !== polygonB.length) {
    // bail out of the interpolation
    return polygonB
  }

  return polygonA.map((pointA, i) => {
    const pointB = polygonB[i]
    return {
      x: pointA.x + (pointB.x - pointA.x) * t,
      y: pointA.y + (pointB.y - pointA.y) * t,
    }
  })
}

/**
 * Finds the best cyclic alignment of polygonA to align with polygonB by
 * minimizing the sum of squared distances between corresponding points.
 *
 * Assumes both polygons have points in consistent cyclic order. It seems to
 * work fine, but if we notice issues we may want to look at the hungarian
 * algorithm (bipartite matching).
 */
export function findCircularAlignment(source: Polygon, reference: Polygon): Polygon {
  let bestOffset = 0
  let minCost = Infinity

  // try all possible cyclic rotations of polygonA
  for (let offset = 0; offset < source.length; offset++) {
    let cost = 0

    for (let i = 0; i < source.length; i++) {
      const aIndex = (i + offset) % source.length
      const p1 = source[aIndex]
      const p2 = reference[i]
      const deltaX = p1.x - p2.x
      const deltaY = p1.y - p2.y
      const distSquared = deltaX * deltaX + deltaY * deltaY
      cost += distSquared
    }

    if (cost < minCost) {
      minCost = cost
      bestOffset = offset
    }
  }

  return source.map((_, i) => source[(i + bestOffset) % source.length])
}

type EdgeWithMetrics = {
  start: Point
  end: Point
  lengthSquared: number
  /** a measure of how horizontal the edge is. 1 is perfectly horizontal */
  horizontalness: number
  /** a measure of how close to the top of screen the edge is */
  topness: number
}

const EDGE_LENGTH_SIMILARITY_THRESHOLD = 0.7

export type BestTextEdgeResult = {
  primary: Edge
  opposite: Edge
}

/**
 * Choose the best edge for text placement, and return its geometric opposite.
 *
 * When two top-facing edges are roughly similar in length, it's probably a 2D
 * barcode, or maybe a tall 1D barcode (is that possible?), and the text would
 * look fine along the topmost edge. But if the topmost  edge is much shorter
 * than the other top edge, then we should prefer that longer edge because it's
 * probably a 1D barcode that should have the text rendered along the longer
 * edge.
 */
export function findBestTextEdge(polygon: Polygon): BestTextEdgeResult {
  const edges: EdgeWithMetrics[] = []

  for (let i = 0; i < polygon.length; i++) {
    const start = polygon[i]
    const end = polygon[(i + 1) % polygon.length]

    const deltaX = end.x - start.x
    const deltaY = end.y - start.y
    const lengthSquared = deltaX * deltaX + deltaY * deltaY
    const horizontalness = (deltaX * deltaX) / lengthSquared
    const topness = start.y + end.y

    edges.push({ start, end, lengthSquared, horizontalness, topness })
  }

  const sorted = [...edges].sort((a, b) => a.topness - b.topness)
  const firstCandidate = sorted[0]
  const secondCandidate = sorted[1]

  // check if lengths are similar
  const lengthRatio =
    firstCandidate.lengthSquared < secondCandidate.lengthSquared
      ? firstCandidate.lengthSquared / secondCandidate.lengthSquared
      : secondCandidate.lengthSquared / firstCandidate.lengthSquared

  let primary: EdgeWithMetrics
  if (lengthRatio >= EDGE_LENGTH_SIMILARITY_THRESHOLD) {
    primary =
      firstCandidate.horizontalness >= secondCandidate.horizontalness
        ? firstCandidate
        : secondCandidate
  } else {
    primary =
      firstCandidate.lengthSquared >= secondCandidate.lengthSquared
        ? firstCandidate
        : secondCandidate
  }

  const primaryIndex = edges.indexOf(primary)
  const oEdge = edges[(primaryIndex + 2) % edges.length]

  // normalize opposite to travel in the same direction as primary
  const primaryAngle = calculateEdgeAngle(primary)
  const oppositeAngle = calculateEdgeAngle(oEdge)
  const opposite: Edge =
    Math.cos(oppositeAngle - primaryAngle) < 0
      ? { start: oEdge.end, end: oEdge.start }
      : { start: oEdge.start, end: oEdge.end }

  return { primary, opposite }
}

export function calculateEdgeAngle(edge: Edge): number {
  const deltaX = edge.end.x - edge.start.x
  const deltaY = edge.end.y - edge.start.y
  return Math.atan2(deltaY, deltaX)
}

export function calculateTextPosition(origin: Point, angle: number, offset: number = 10): Point {
  const normalAngle = angle - Math.PI / 2
  const offsetX = Math.cos(normalAngle) * offset
  const offsetY = Math.sin(normalAngle) * offset

  return {
    x: origin.x + offsetX,
    y: origin.y + offsetY,
  }
}
